import { useState, useEffect, useCallback, useRef } from 'react'
import { LiveTranscript } from './components/LiveTranscript'
import { ProfileCard } from './components/ProfileCard'
import { CallHistorySidebar } from './components/CallHistorySidebar'
import { CalendarView } from './components/CalendarView'
import { ConflictAlertModal } from './components/ConflictAlertModal'
import { CancellationModal } from './components/CancellationModal'
import { AdminPanel } from './components/AdminPanel'
import { useCallStore } from './stores/callStore'
import { useWebSocket } from './hooks/useWebSocket'

const API_URL = (import.meta.env.VITE_API_URL as string) || 'https://salonflow-api.empty-mud-1234.workers.dev'

type ViewMode = 'live' | 'calendar'

export default function App() {
  const { 
    transcript, 
    callDuration, 
    isCallActive, 
    reset, 
    startCall,
    fields,
    conflictModal,
    hideConflictModal,
    updateField,
    currentSessionId,
    cancellationModal,
    hideCancellationModal,
    detectedIntent,
    isAIPaused,
    toggleAIPaused,
    serviceDuration
  } = useCallStore()
  
  const [view, setView] = useState<ViewMode>('live')
  const [isBooking, setIsBooking] = useState(false)
  const [callSid, setCallSid] = useState<string>('')
  const [isListening, setIsListening] = useState(false)
  const [statusMessage, setStatusMessage] = useState('')
  const [showAdminPanel, setShowAdminPanel] = useState(false)
  const lastCallIdRef = useRef<string>('')
  
  // WebSocket connection
  const { isConnected, error: wsError } = useWebSocket(callSid || undefined)

  // Poll for latest call
  const pollForCall = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/debug/latest-call`)
      const data = await response.json()
      
      if (data.callId && data.callId !== lastCallIdRef.current) {
        console.log('Found NEW call:', data.callId)
        lastCallIdRef.current = data.callId
        setCallSid(data.callId)
        startCall()
        reset()
        setStatusMessage('Call connected - listening...')
      }
    } catch (err) {
      console.error('Poll error:', err)
    }
  }, [startCall, reset])

  // Continuous polling while listening
  useEffect(() => {
    if (!isListening) return
    
    pollForCall()
    const interval = setInterval(pollForCall, 1000)
    return () => clearInterval(interval)
  }, [isListening, pollForCall])

  const handleBookAppointment = async () => {
    setIsBooking(true)
    
    const dateField = fields.date
    const timeField = fields.time
    const serviceField = fields.service
    const stylistField = fields.requestedStylist
    const firstNameField = fields.firstName
    const phoneField = fields.phone
    const emailField = fields.email
    const lastNameField = fields.lastName
    const notesField = fields.notes
    
    // Validate required fields
    if (!dateField?.value || !timeField?.value || !serviceField?.value) {
      alert('Please fill in date, time, and service fields')
      setIsBooking(false)
      return
    }
    
    if (!firstNameField?.value || !phoneField?.value) {
      alert('Customer name and phone are required')
      setIsBooking(false)
      return
    }

    try {
      // 1. Check availability with stylist info and actual service duration
      const bookingDuration = serviceDuration || 60
      console.log(`[Booking] Using duration: ${bookingDuration} mins for services: ${serviceField.value}`)
      
      const availResponse = await fetch(`${API_URL}/api/appointments/check-availability`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          date: dateField.value,
          time: timeField.value,
          duration: bookingDuration,
          stylistName: stylistField?.value || undefined
        })
      })
      
      const availResult = await availResponse.json()
      
      if (!availResult.available) {
        useCallStore.getState().showConflictModal({
          conflict: availResult.conflicts[0],
          suggestions: availResult.suggestions
        })
        setIsBooking(false)
        return
      }

      // 2. Create or find customer
      const customerResponse = await fetch(`${API_URL}/api/customers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: firstNameField.value,
          lastName: lastNameField?.value || '',
          phone: phoneField.value,
          email: emailField?.value || '',
          notes: notesField?.value || ''
        })
      })
      
      const customerResult = await customerResponse.json()
      
      if (!customerResult.id) {
        alert('Failed to create customer record')
        setIsBooking(false)
        return
      }

      // 3. Create the actual appointment with correct duration
      const appointmentResponse = await fetch(`${API_URL}/api/appointments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerId: customerResult.id,
          service: serviceField.value,
          date: dateField.value,
          time: timeField.value,
          duration: bookingDuration,
          stylistName: stylistField?.value || undefined,
          notes: notesField?.value || ''
        })
      })
      
      const appointmentResult = await appointmentResponse.json()
      
      if (appointmentResult.conflict) {
        // Conflict detected during actual booking
        useCallStore.getState().showConflictModal({
          conflict: appointmentResult.conflicts[0],
          suggestions: appointmentResult.suggestions
        })
        setIsBooking(false)
        return
      }
      
      if (!appointmentResult.id && !appointmentResult.success) {
        alert(`Booking failed: ${appointmentResult.error || 'Unknown error'}`)
        setIsBooking(false)
        return
      }

      // Success!
      const smsNote = appointmentResult.smsStatus === 'sent' 
        ? 'SMS confirmation sent to customer.' 
        : ''
      alert(`Appointment booked successfully! ${smsNote}`)
      
    } catch (err) {
      console.error('Booking failed:', err)
      alert('Booking failed - please try again')
    }
    
    setIsBooking(false)
  }

  const handleReset = () => {
    reset()
    setCallSid('')
    lastCallIdRef.current = ''
    setStatusMessage('')
  }

  const handleAnswerPhone = () => {
    setIsListening(true)
    setCallSid('')
    lastCallIdRef.current = ''
    setStatusMessage('Ready - waiting for incoming call...')
    reset()
  }

  const handleHangUp = () => {
    setIsListening(false)
    setCallSid('')
    setStatusMessage('')
  }

  const handleSelectAlternativeSlot = (slot: { date: string; time: string }) => {
    // Update the date and time fields with the selected alternative
    updateField('date', {
      value: slot.date,
      confidence: 100,
      status: 'confirmed',
      updatedAt: Date.now()
    })
    updateField('time', {
      value: slot.time,
      confidence: 100,
      status: 'confirmed',
      updatedAt: Date.now()
    })
    hideConflictModal()
  }

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Header with view toggle */}
      <div className="border-b border-gray-200 bg-white">
        <div className="flex items-center justify-between px-6 py-3">
          <div className="flex items-center gap-4">
            <h1 className="text-xl font-bold text-gray-900">SalonFlow</h1>
            
            {/* View Toggle */}
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setView('live')}
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  view === 'live' 
                    ? 'bg-white text-gray-900 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <span className="flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  Live Call
                </span>
              </button>
              <button
                onClick={() => setView('calendar')}
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  view === 'calendar' 
                    ? 'bg-white text-gray-900 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <span className="flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  Calendar
                </span>
              </button>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Admin Panel Button */}
            <button
              onClick={() => setShowAdminPanel(true)}
              className="px-3 py-1.5 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Admin
            </button>

            {/* New Call button - shown when viewing historical */}
            {currentSessionId && !isCallActive && !isConnected && (
              <button
                onClick={handleReset}
                className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                New Call
              </button>
            )}
            
            {/* Live call status */}
            {(isCallActive || isConnected) && (
              <div className="flex items-center gap-2">
                {/* Pause/Resume AI Listening Button */}
                <button
                  onClick={toggleAIPaused}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                    isAIPaused 
                      ? 'bg-amber-100 text-amber-800 hover:bg-amber-200 ring-2 ring-amber-300' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  title={isAIPaused ? 'Resume AI form filling' : 'Pause AI form filling'}
                >
                  {isAIPaused ? (
                    <>
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                      Resume AI
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                      </svg>
                      Pause AI
                    </>
                  )}
                </button>
                
                <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${
                  isAIPaused 
                    ? 'bg-amber-100 text-amber-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  <span className={`h-2 w-2 rounded-full ${
                    isAIPaused ? 'bg-amber-500' : 'bg-red-500 animate-pulse'
                  }`} />
                  <span className="text-sm font-medium">
                    {isAIPaused ? 'AI Paused' : 'Live Call'} {callDuration > 0 && `(${Math.floor(callDuration / 60)}:${(callDuration % 60).toString().padStart(2, '0')})`}
                  </span>
                </div>
                
                {/* Intent indicator */}
                {detectedIntent && detectedIntent !== 'booking' && (
                  <div className={`px-3 py-1.5 rounded-full text-sm font-medium ${
                    detectedIntent === 'cancellation' 
                      ? 'bg-amber-100 text-amber-800' 
                      : detectedIntent === 'reschedule'
                      ? 'bg-purple-100 text-purple-800'
                      : 'bg-blue-100 text-blue-800'
                  }`}>
                    {detectedIntent === 'cancellation' && 'Cancellation'}
                    {detectedIntent === 'reschedule' && 'Reschedule'}
                    {detectedIntent === 'inquiry' && 'Inquiry'}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Call History Sidebar - always visible */}
        <CallHistorySidebar onCallSelect={() => setView('live')} />

        {/* Main content area */}
        <main className="flex-1 overflow-hidden">
          {view === 'live' ? (
            <div className="h-full flex">
              {/* Left Panel - Transcript */}
              <div className="w-2/5 border-r border-gray-200">
                <LiveTranscript 
                  messages={transcript} 
                  isLive={isCallActive || isConnected}
                  isHistorical={!!currentSessionId && !isCallActive && !isConnected}
                />
              </div>

              {/* Right Panel - Profile Card */}
              <div className="flex-1">
                <ProfileCard 
                  onBookAppointment={handleBookAppointment}
                  isBooking={isBooking}
                />
              </div>
            </div>
          ) : (
            <CalendarView />
          )}
        </main>
      </div>

      {/* Bottom Control Bar - only show in live view */}
      {view === 'live' && (
        <div className="border-t border-gray-200 bg-white px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Left side - Call controls */}
            <div className="flex items-center gap-4">
              {!isListening ? (
                <button
                  onClick={handleAnswerPhone}
                  className="px-8 py-3 bg-green-600 text-white rounded-lg font-semibold text-lg hover:bg-green-700 transition-all shadow-lg hover:shadow-xl flex items-center gap-2"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  Answer Phone
                </button>
              ) : (
                <button
                  onClick={handleHangUp}
                  className="px-8 py-3 bg-red-600 text-white rounded-lg font-semibold text-lg hover:bg-red-700 transition-all shadow-lg flex items-center gap-2"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 8l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2M5 3a2 2 0 00-2 2v1c0 8.284 6.716 15 15 15h1a2 2 0 002-2v-3.28a1 1 0 00-.684-.948l-4.493-1.498a1 1 0 00-1.21.502l-1.13 2.257a11.042 11.042 0 01-5.516-5.517l2.257-1.128a1 1 0 00.502-1.21L9.228 3.683A1 1 0 008.28 3H5z" />
                  </svg>
                  Hang Up
                </button>
              )}

              <button
                onClick={handleReset}
                className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
              >
                Clear
              </button>
            </div>

            {/* Center - Status */}
            <div className="flex items-center gap-3">
              {isConnected && (
                <div className="flex items-center gap-2 px-4 py-2 bg-green-100 text-green-800 rounded-full">
                  <span className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
                  <span className="font-medium">Live Call</span>
                </div>
              )}
              
              {isListening && !isConnected && (
                <div className="flex items-center gap-2 px-4 py-2 bg-amber-100 text-amber-800 rounded-full">
                  <span className="h-2 w-2 rounded-full bg-amber-500 animate-pulse" />
                  <span className="font-medium">{statusMessage}</span>
                </div>
              )}

              {!isListening && !isConnected && (
                <div className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-600 rounded-full">
                  <span className="h-2 w-2 rounded-full bg-gray-400" />
                  <span className="font-medium">Ready</span>
                </div>
              )}

              {wsError && (
                <div className="text-red-600 text-sm ml-4">Error: {wsError}</div>
              )}
            </div>

            {/* Right side - Phone number */}
            <div className="text-right">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Incoming Line</p>
              <p className="text-lg font-mono font-bold text-gray-900">+1 276 400 9096</p>
            </div>
          </div>
        </div>
      )}

      {/* Conflict Alert Modal */}
      {conflictModal.isOpen && conflictModal.conflict && (
        <ConflictAlertModal
          conflict={conflictModal.conflict}
          suggestions={conflictModal.suggestions}
          onSelectSlot={handleSelectAlternativeSlot}
          onClose={hideConflictModal}
        />
      )}

      {/* Admin Panel Modal */}
      {showAdminPanel && (
        <AdminPanel onClose={() => setShowAdminPanel(false)} />
      )}

      {/* Cancellation Flow Modal - appears when customer wants to cancel */}
      <CancellationModal
        isOpen={cancellationModal.isOpen}
        customerPhone={cancellationModal.customerPhone}
        customerName={cancellationModal.customerName}
        onClose={hideCancellationModal}
        onNavigateToCalendar={() => {
          hideCancellationModal()
          setView('calendar')
        }}
      />
    </div>
  )
}
